import {types, flow, getSnapshot, applySnapshot} from 'mobx-state-tree';
import { navigate } from 'js/services/Network';
import { pick } from 'lodash';
import moment from 'moment';

import { makeRequest, url, errorResponse } from 'js/services/Network';
import { castEventToValue } from 'js/services/FormTools';
import analytics from 'js/services/Analytics';
import AnonymousId from 'js/services/AnonymousId';
import PlaidLink from 'js/services/PlaidLink';

import AppUser from 'js/stores/AppUser';
import SteppedPage from 'js/stores/SteppedPage';

import PurchaseToken from '../types/PurchaseToken';
import Address from '../types/Address';
import BankAccount from '../types/BankAccount';
import Time from '../types/Time';
import CreditCard from "js/stores/types/CreditCard";

const TokenPage = types
  .model('TokenPage', {
    alias: types.string,
    pickupModalIsVisible: false,
    modalIsVisible: false,
    choicesModalIsVisible: false,

    purchase_token: types.maybe(PurchaseToken),
    phone_number: types.optional(types.string, () => AppUser.phone_number || ''),
    full_name: types.optional(types.string, () => AppUser.name || ''),
    size: '',
    delivery: types.optional(types.enumeration(['shipment', 'scheduled-shipment', 'in-person']), 'shipment'),
    pickup_time: types.optional(Time, {}),
    address: types.optional(Address, {}),
    bankAccount: types.optional(BankAccount, {}),
    bank_verification_flow: 'system',

    debitCard: types.optional(CreditCard, {}),
    debit_card_error: types.optional(types.union(types.string, types.null), null),
    isSavingCard: false,

    plaidError: types.maybeNull(types.string),
    plaidIsStarted: false,
    savingPlaidItem: false,
  })
  .views(self => ({
    get isBusy() {
      return self.plaidIsStarted || self.savingPlaidItem;
    },
  }))
  .actions(self => ({
    getStepRoute(step) {
      return url('token-page', {
        alias: self.alias,
        step,
      });
    },

    initStep(step) {
      self.pickupModalIsVisible = false;
      self.modalIsVisible = false;
      return self;
    },

    load: flow(function* load() {
      const response = yield makeRequest('token-data', self);

      self.phone_number = response.data.phone_number;
      self.full_name = response.data.full_name;
      self.purchase_token = response.data.purchase_token;
      self.bankAccount = response.data.bankAccount;
      self.address = response.data.address;

      //analytics.page('seller token page');
      return response;
    }),

    submit: flow(function* submit() {
      let response;
      if (!self.isSubmitting) {
        self.isSubmitting = true;
        self.error = null;

        try {
          response = yield makeRequest(
            'token-complete',
            self,
            pick(self, [
              'delivery',
              'pickup_time',
              'phone_number',
              'full_name',
              'address',
              'size',
              'bankAccount',
            ]),
          );
          navigate(url('status-page', self));
        } catch (error) {
          self.error = error.response.data.message || error.response.statusText;
        }

        self.isSubmitting = false;
      }
      return response;
    }),

    saveDebitCard: flow(function* saveDebitCard(values) {
      let response;

      if (!self.isSavingCard) {
        self.debit_card_error = null;
        self.isSavingCard = true;
        try {
          values.anonymous_id = AnonymousId;
          response = yield makeRequest('debit-card-save', null, values);
          applySnapshot(self, { ...getSnapshot(self), ...response.data });
          self.debitCard.setId(response.data.id);
          self.closeModals();
        } catch (error) {
          self.debit_card_error = errorResponse(error);
        }
      }
      self.isSavingCard = false;

      return response;
    }),

    setDelivery(delivery = 'shipped') {
      self.delivery = delivery;
      return self;
    },
    setFullName(full_name) {
      self.full_name = castEventToValue(full_name);
      return self;
    },
    setPhoneNumber(phone_number) {
      self.phone_number = castEventToValue(phone_number);
      return self;
    },
    setSize(size) {
      self.size = castEventToValue(size);
      return self;
    },
    addressSaved(savedAddress) {
      self.address.setAddress(getSnapshot(savedAddress));
      self.modalIsVisible = false;
    },
    receivingAccountSaved(savedReceivingAccount) {
      self.bankAccount.setBankAccount({ ...getSnapshot(savedReceivingAccount), id: 0 });
      self.modalIsVisible = false;
    },

    meetInPerson() {
      //analytics.track('SAC_clicked meet buyer');
      self.delivery = 'in-person';
      self.nextStep();
    },
    openPickupModal() {
      if (moment().isBetween(moment().hour(3), moment().hour(18))) {
        //analytics.track('SAC_viewed pick up time');
        self.pickupModalIsVisible = true;
      } else {
        self.nextStep();
      }
      return self;
    },
    closePickupModal() {
      self.pickupModalIsVisible = false;
      return self;
    },
    openModal() {
      self.modalIsVisible = true;
      return self;
    },


    closeModal() {
      self.modalIsVisible = false;
      return self;
    },
    closeModals() {
      self.modalIsVisible = false;
      self.choicesModalIsVisible = false;
      return self;
    },

    showChoicesModal() {
      //editReceivingAccount
      self.choicesModalIsVisible = true;
    },

    showBankAccountModal() {
      if (app.bank_verification_flow === 'plaid' && self.bank_verification_flow === 'system') {
        // Verify/connect bank account using Plaid
        self.startPlaid();
      } else {
        // Verify/connect bank account using old flow
        self.modalIsVisible = true;
      }

      return self;
    },

    startPlaid: flow(function* startPlaid() {
      // Start the Plaid bank account entry flow
      if (!self.isBusy) {
        let plaidItem;
        self.plaidError = null;
        self.plaidIsStarted = true;

        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        try {
          plaidItem = (yield new PlaidLink(app.plaid).open()).metadata;
        } catch (error) {}
        window.scrollTo(scrollX, scrollY);
        self.plaidIsStarted = false;

        if (plaidItem) {
          self.savingPlaidItem = true;
          try {
            self.bankAccount = (yield makeRequest('plaid-item-save', null, {
              anonymous_id: AnonymousId,
              ...pick(plaidItem, ['account', 'institution', 'public_token', 'link_session_id']),
            })).data;
          } catch (error) {
            self.plaidError = errorResponse(error);
          }
          self.savingPlaidItem = false;
        }
      }
    }),
  }));

export default types.compose(
  SteppedPage,
  TokenPage,
);
